import { v4 as uuidv4 } from "uuid"

import { contractorUserRoleOptions } from "constants/SelectFieldOptions"
import SubscriptionPlanTitlesWithPrefix from "constants/SubscriptionPlanTitlesWithPrefix"
import { ApiTypes, UITypes } from "types"

import SensorTypeCode from "../types/enums/SensorTypeCode"
import SensorWorkStatus from "../types/enums/SensorWorkStatus"
import SubscriptionPlan from "../types/enums/SubscriptionPlan"
import { capitalizeFirstLetter } from "./capitalizeFirstLetter"
import { parseDateField } from "./dateUtils"
import { getProductTypeLabel } from "./getProductTypeLabel"
import { getSensorProductType } from "./getSensorProductType"
import decodeToMarkDown from "./markdown/decodeToMarkDown"
import { parseDateStringToUTCString } from "./parseDateStringToUTCString"
import { parseSystemType } from "./parseSystemType"
import { transformString } from "./transformString"
import { transformText } from "./transformText"

export const convertDataToOptions = <T extends Record<Y | Z, string | number>, Y extends keyof T, Z extends keyof T>(
  data: { data: T[] | null } | undefined,
  valueName: Y,
  labelName: Z,
): UITypes.Option[] => {
  if (!data?.data?.length) return []

  return data.data.map((item: T) => ({
    value: item && item[valueName],
    label: item && item[labelName]?.toString(),
  }))
}

export const convertStringArrayToOptions = (data: string[]): UITypes.Option[] => [
  ...data.map(item => ({
    value: item,
    label: item,
  })),
]

// TABLES
export const convertVSMOrdersNewToTableRows = (data?: ApiTypes.Model.VSManagerOrderNew[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []
  return data.map(({ id, create_date = "", status, email, phone_number, company_name, items, contractor_id }) => {
    const singleItem = items.length === 1 ? items[0] : null

    return {
      group_toggle: { hidden: !!singleItem },
      item_id: { value: singleItem?.id },
      item_status: { value: status, csvValue: singleItem?.status },
      company_name: { value: company_name },
      create_date: { value: parseDateField(create_date) },
      shipping_date: { value: parseDateField(singleItem?.shipping_date) },
      fulfillment_date: { value: parseDateField(singleItem?.fulfillment_date) },
      product_type: { value: getProductTypeLabel(singleItem?.product_type) },
      quantity: { value: singleItem?.quantity },
      item_type: {
        value: singleItem?.item_type ? transformText(singleItem.item_type) : undefined,
      },
      warehouse: { value: singleItem?.warehouse },
      phone_number: { value: phone_number },
      email: { value: email },
      contractor_id: { value: contractor_id },
      status_updated: { value: parseDateField(singleItem?.status_updated) },
      subRows: singleItem
        ? []
        : items.map(item => ({
            group_toggle: { hidden: true },
            contractor_id: { value: "" },
            item_status: { value: status },
            company_name: { value: "" },
            item_id: { value: item.id },
            create_date: { value: null },
            shipping_date: {
              value: parseDateField(item.shipping_date),
            },
            fulfillment_date: {
              value: parseDateField(item.fulfillment_date),
            },
            product_type: { value: getProductTypeLabel(item.product_type) },
            quantity: { value: item.quantity },
            item_type: {
              value: item.item_type ? transformText(item.item_type) : undefined,
            },
            warehouse: { value: item.warehouse },
            phone_number: { value: "" },
            email: { value: "" },
            status_updated: { value: parseDateField(item?.status_updated) },
            rowSettings: { id: item.id, item: [item] },
          })),
      rowSettings: {
        id,
        item: items,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertEnterpriseOrdersNewToTableRows = (
  data?: ApiTypes.Model.EnterpriseOrderNew[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, create_date = "", status, contractor, company_name, items, contractor_id }) => {
    const singleItem = items.length === 1 ? items[0] : null
    const contractorName = contractor || company_name

    return {
      group_toggle: { hidden: !!singleItem },
      item_id: { value: singleItem?.id },
      item_status: { value: status, csvValue: singleItem?.status },
      company_name: { value: contractorName },
      create_date: { value: parseDateField(create_date) },
      product_type: { value: getProductTypeLabel(singleItem?.product_type) },
      quantity: { value: singleItem?.quantity },
      warehouse: { value: singleItem?.warehouse },
      contractor_id: { value: contractor_id },
      status_updated: { value: parseDateField(singleItem?.status_updated) },
      subRows: singleItem
        ? []
        : items.map(item => ({
            group_toggle: { hidden: true },
            item_status: { value: item.status },
            company_name: { value: "" },
            item_id: { value: item.id },
            create_date: { value: null },
            product_type: { value: getProductTypeLabel(item.product_type) },
            quantity: { value: item.quantity },
            warehouse: { value: item.warehouse },
            contractor_id: { value: "" },
            status_updated: { value: parseDateField(item.status_updated) },
            rowSettings: { id: item.id, item: [item] },
          })),
      rowSettings: {
        id,
        item: items,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertOrdersNewToTableRows = (
  data?: ApiTypes.Model.OrderNew[],
  showV2 = false,
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []
  const useContractorOverride = !showV2

  return data.map(({ id, items, create_date, status }) => {
    const singleItem = items.length === 1 ? items[0] : null

    return {
      item_id: { value: singleItem?.id },
      group_toggle: { hidden: !!singleItem },
      create_date: { value: parseDateField(create_date) },
      shipping_date: { value: parseDateField(singleItem?.shipping_date) },
      fulfillment_date: { value: parseDateField(singleItem?.fulfillment_date) },
      quantity: { value: singleItem?.quantity },
      item_type: {
        value: singleItem?.item_type ? transformText(singleItem.item_type) : undefined,
      },
      warehouse: {
        value: singleItem?.warehouse,
      },
      item_status: { value: status, csvValue: singleItem?.status },
      product_type: { value: getProductTypeLabel(singleItem?.product_type, useContractorOverride) },
      rowSettings: {
        id,
        item: items,
      },
      subRows: singleItem
        ? []
        : items.map(item => ({
            group_toggle: { hidden: true },
            item_id: { value: item.id },
            create_date: { value: null },
            shipping_date: {
              value: parseDateField(item.shipping_date),
            },
            fulfillment_date: {
              value: parseDateField(item.fulfillment_date),
            },
            quantity: { value: item.quantity },
            item_type: {
              value: item.item_type ? transformText(item.item_type) : undefined,
            },
            warehouse: {
              value: item.warehouse,
            },
            item_status: { value: item.status },
            product_type: { value: getProductTypeLabel(item.product_type, useContractorOverride) },
            rowSettings: {
              id: item.id,
              item: [item],
            },
          })),
    }
  })
}

export const convertClientsToTableRows = (data?: ApiTypes.Model.Client[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, full_name, devices, email, phone_number, membership, notes, app_activated }) =>
      ({
        checkbox: {},
        email: { value: email },
        devices_count: { value: devices },
        phone: { value: phone_number },
        full_name: { value: full_name },
        id: { value: id },
        membership: { value: membership },
        notes: { value: notes || "" },
        app_activated: { value: app_activated ? "yes" : "no" },
        rowSettings: {
          id,
          isEnable: devices ? true : false,
          item: {
            client_id: id,
            devices_count: devices,
          },
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertContractorsToTableRows = (data?: ApiTypes.Model.Contractor[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    contractor =>
      ({
        name: { value: contractor.name },
        state: { value: contractor.state?.name },
        country: { value: contractor.country?.name },
        subscription_plan: {
          value: SubscriptionPlanTitlesWithPrefix[contractor.subscription_plan || SubscriptionPlan.BASIC],
        },
        brand: { value: contractor.brand },
        purchased: { value: contractor.purchased_devices },
        installed: { value: contractor.installed_devices },
        install_7_day: { value: contractor.install_7_day },
        active_mobile: { value: contractor.active_mobile },
        inventory: { value: contractor.inventory_devices },
        isEnabled: { value: contractor.is_enabled ? "1" : "0" },
        account_status_id: { value: contractor.account_status ? String(contractor.account_status.id) : "" },
        warranty: { value: contractor.warranty },
        create_date: { value: parseDateField(contractor.created_at) },
        last_login: {
          value: parseDateField(contractor.last_login),
        },
        rowSettings: {
          id: Number(contractor.id),
          isEnabled: contractor.is_enabled,
          item: contractor,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertAdminAlertsToTableRows = (data?: ApiTypes.Model.Alert[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, sn, country, address1, city, state, date, status, type, full_name, phone_number, email, location }) =>
      ({
        checkbox: {},
        type: { value: transformText(type) },
        date: { value: parseDateField(date) },
        sn: { value: sn },
        status: { value: status },
        full_name: { value: full_name },
        phone_number: { value: phone_number },
        email: { value: email },
        location: { value: location },
        country: { value: country },
        address1: { value: address1 },
        city: { value: city },
        state: { value: state },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertEnterpriseAlertsToTableRows = (data?: ApiTypes.Model.EnterpriseAlert[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return convertAdminAlertsToTableRows(data).map(
    (row, index) =>
      ({
        ...row,
        contractor_name: { value: data[index].contractor },
        rowSettings: {
          ...row.rowSettings,
          item: data[index],
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertAlertsToTableRows = (data?: ApiTypes.Model.Alert[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({
      id,
      sn,
      country,
      address1,
      city,
      state,
      date,
      datetime,
      status,
      type,
      full_name,
      phone_number,
      email,
      location,
    }) =>
      ({
        checkbox: {},
        type: { value: transformText(type) },
        date: { value: parseDateField(date) },
        sn: { value: sn },
        status: { value: status },
        full_name: { value: full_name },
        phone_number: { value: phone_number },
        email: { value: email },
        location: { value: location },
        country: { value: country },
        address1: { value: address1 },
        city: { value: city },
        state: { value: state },
        datetime: { value: datetime },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertMessageTemplatesToTableRows = (data?: ApiTypes.Model.MessageTemplate[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, title, content_rich, created_at }) => ({
    title: { value: title },
    created_at: { value: parseDateField(created_at) },
    message: { value: content_rich ? decodeToMarkDown(content_rich) : "" },
    id: { value: id },
    rowSettings: {
      id,
    },
  }))
}

export const convertTemplatesLibraryToTableRows = (
  data?: ApiTypes.Model.TemplatesLibrary[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data?.map(group => ({
    id: { value: group?.name, groupHeader: true },
    title: { value: "", displayNone: true },
    message: { value: "", displayNone: true },
    rowSettings: {
      id: group?.id + 1000,
      isEnable: false,
    },
    subRows:
      group?.items?.map(items => ({
        id: { value: items?.id },
        title: { value: items?.title },
        message: { value: items?.description_rich ? decodeToMarkDown(items?.description_rich) : "" },
        rowSettings: {
          id: items?.id,
        },
      })) || [],
  }))
}

export const convertWarehousesToTableRows = (data?: ApiTypes.Model.Warehouse[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, name, address, phone_number, zip, state, devices_qty, managers }) =>
      ({
        name: { value: name },
        devices_qty: { value: devices_qty },
        address: { value: address },
        zip: { value: zip?.code },
        state: { value: state?.name },
        phone_number: { value: phone_number },
        manager: { value: managers },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDevicesAndScheduledMessagesToTableRows = (
  data?: ApiTypes.Model.Device[],
  devicesMessagesData?: ApiTypes.Model.DevicesMessages[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(device => {
    const deviceMessages = devicesMessagesData?.find(item => item?.device_id === device.id)
    const singleMessage = deviceMessages?.messages?.length === 1 ? deviceMessages?.messages[0] : null

    return {
      group_toggle: { hidden: singleMessage || !deviceMessages?.messages?.length ? true : false },
      checkbox: {},
      email: { value: device.email },
      sn: { value: device.sn },
      country: { value: device.country?.name },
      city: { value: device.city?.name },
      zip_code: { value: device.zip?.code },
      state: { value: device.state?.name },
      option: { value: device.option ? capitalizeFirstLetter(device.option).replace("_", "-") : "" },
      activation_date: { value: parseDateField(device.activation_date) },
      homeowner_name: { value: device.homeowner_name },
      membership: { value: device.membership },
      membership_name: { value: device.memberships },
      residence_type: { value: device.residence_type?.name },
      system_type: { value: device.system_type },
      accessories: { value: device.accessories },
      installation_type: { value: device.hvac },
      system_age: { value: device.system_age },
      app_activated: { value: device.app_activated ? "yes" : "no" },
      schedule: { value: singleMessage?.scheduled_at },
      scheduled_message: { value: singleMessage?.message_template_id },
      subRows:
        deviceMessages && !singleMessage
          ? deviceMessages.messages.map(message => ({
              group_toggle: { hidden: true },
              checkbox: { hidden: true },
              email: {},
              sn: {},
              country: {},
              city: {},
              zip_code: {},
              state: {},
              option: {},
              activation_date: {},
              homeowner_name: {},
              membership: {},
              membership_name: {},
              residence_type: {},
              system_type: {},
              accessories: {},
              installation_type: {},
              system_age: {},
              app_activated: {},
              schedule: { value: message?.scheduled_at },
              scheduled_message: { value: message?.message_template_id },
              rowSettings: {
                id: message?.message_id,
                item: message,
              },
            }))
          : [],
      rowSettings: {
        id: device.id,
        item: {
          ...singleMessage,
          client_id: device.client_id,
          sn: device.sn,
          email: device.email,
          phisical_status: device.phisical_status,
        },
      },
    } as UITypes.TableBodyRow
  })
}

export const convertDevicesToTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        email: { value: device.email },
        sn: { value: device.sn },
        country: { value: device.country?.name },
        city: { value: device.city?.name },
        zip_code: { value: device.zip?.code },
        state: { value: device.state?.name },
        option: { value: device.option ? capitalizeFirstLetter(device.option).replace("_", "-") : "" },
        activation_date: { value: parseDateField(device.activation_date) },
        homeowner_name: { value: device.homeowner_name },
        membership: { value: device.membership },
        membership_name: { value: device.memberships },
        residence_type: { value: device.residence_type?.name },
        system_type: { value: device.system_type },
        accessories: { value: device.accessories },
        installation_type: { value: device.hvac },
        system_age: { value: device.system_age },
        rowSettings: {
          id: device.id,
          item: device,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDevicesToContractorDevicesTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        sn: { value: device.sn },
        phisical_status: {
          value: device.phisical_status,
        },
        email: { value: device.email },
        homeowner_name: { value: device.homeowner_name },
        country: { value: device.country?.name },
        city: { value: device.city?.name },
        state: { value: device.state?.name },
        zip_code: { value: device.zip?.code },
        activation_date: { value: parseDateField(device.activation_date) },
        address: {
          value: `${device.address ? device.address : ""} ${device.state?.name ? device.state?.name : ""} ${device.city?.name ? device.city?.name : ""}`,
        },
        installation_type: { value: device.hvac },
        alerts: { value: device.alerts },
        membership: { value: device.membership },
        membership_name: { value: device.memberships },
        system_type: { value: parseSystemType(device.system_type) },
        accessories: { value: device.accessories },
        system_age: { value: device.system_age },
        app_activated: { value: device.app_activated ? "yes" : "no" },
        notes: { value: device.notes },
        rowSettings: {
          id: device.id,
          item: device,
        },
      }) as UITypes.TableBodyRow,
  )
}

const SENSOR_WORK_STATUS_TO_LABEL: Record<string, string> = {
  [SensorWorkStatus.PAIRED]: "Connected",
  [SensorWorkStatus.NOT_PAIRED]: "Disconnected",
}

export const convertSensorsTabToTableRows = (data?: ApiTypes.Model.SensorTabThermostat[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(thermostat => {
    const sensors = thermostat.sensors ?? []
    const parsedThermostat = {
      ...thermostat,
      sensors,
    }

    return {
      group_toggle: { hidden: !sensors.length },
      sn: { value: thermostat.sn },
      sensor_type: { value: "" },
      customer_name: { value: thermostat.customer_name },
      customer_email: { value: thermostat.customer_email },
      installation_address: { value: thermostat.installation_address },
      sensor_count: { value: thermostat.sensor_count },
      work_status: { value: "" },
      selected_status: { value: "" },
      subRows: sensors.map(sensor => ({
        group_toggle: { hidden: true },
        sn: { value: sensor.suid },
        sensor_type: { value: getSensorProductType(sensor.type)?.toUpperCase() ?? sensor.type },
        customer_name: { value: "" },
        customer_email: { value: "" },
        installation_address: { value: "" },
        sensor_count: { value: "" },
        work_status: { value: SENSOR_WORK_STATUS_TO_LABEL[sensor.work_status] ?? sensor.work_status },
        selected_status: {
          value: sensor.type === SensorTypeCode.WLS ? "-" : capitalizeFirstLetter(sensor.selected_status),
        },
        rowSettings: { id: sensor.sensor_id, item: { ...sensor, sensors: [] } },
      })),
      rowSettings: { id: thermostat.id, item: parsedThermostat },
    } as UITypes.TableBodyRow
  })
}

export const convertAdminSensorsTabToTableRows = (
  data?: ApiTypes.Model.AdminSensorTabThermostat[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return convertSensorsTabToTableRows(data).map(
    (row, i) =>
      ({
        ...row,
        contractor_name: { value: data[i].contractor_name },
        subRows: row.subRows?.map(sub => ({ ...sub, contractor_name: { value: "" } })),
      }) as UITypes.TableBodyRow,
  )
}

export const convertEnterpriseSensorsTabToTableRows = (
  data?: ApiTypes.Model.EnterpriseSensorTabThermostat[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return convertAdminSensorsTabToTableRows(
    data.map(
      row =>
        ({
          ...row,
          contractor_name: row.contractor,
        }) as ApiTypes.Model.AdminSensorTabThermostat,
    ),
  )
}

export const convertDataToPerformanceTestTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(device => {
    const {
      id,
      email,
      sn,
      perftest,
      homeowner_name,
      country,
      city,
      state,
      zip,
      work_status,
      address,
      hvac,
      alerts,
      alert_rules,
      membership,
      memberships,
      system_type,
      accessories,
      system_age,
      activation_date,
    } = device

    return {
      checkbox: { disabled: perftest?.status?.toLowerCase() === "running" },
      email: { value: email },
      perftest_status: { value: perftest?.status?.toLowerCase() },
      temp_change: { value: { start_temp: perftest?.start, end_temp: perftest?.end } },
      work_status: { value: work_status },
      perftest_label: { value: perftest?.label },
      country: { value: country?.name },
      city: { value: city?.name },
      zip_code: { value: zip?.code },
      state: { value: state?.name },
      homeowner_name: { value: homeowner_name },
      sn: { value: sn },
      address: { value: address || "" },
      installation_type: { value: hvac },
      alerts: { value: alerts },
      alert_rules: { value: alert_rules || [] },
      membership: { value: membership },
      membership_name: { value: memberships },
      system_type: { value: parseSystemType(system_type) },
      accessories: { value: accessories },
      perftest_schedule: { value: perftest?.schedule },
      system_age: { value: system_age },
      activation_date: { value: parseDateField(activation_date) },
      rowSettings: {
        id,
        item: device,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertDataToPerformanceTestHistoryTableRows = (
  data?: ApiTypes.Model.PerformanceTestHistory[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ client, device, perftest }) =>
      ({
        email: { value: client?.email },
        status: { value: perftest?.status?.toLowerCase() },
        temp_change: { value: { start_temp: perftest?.start, end_temp: perftest?.end } },
        time: { value: parseDateField(parseDateStringToUTCString(perftest.time)) },
        label: { value: perftest?.label },
        country: { value: device?.country?.name },
        city: { value: device?.city?.name },
        zip_code: { value: device?.zip?.code },
        state: { value: device?.state?.name },
        homeowner_name: { value: client?.homeowner_name },
        sn: { value: device?.sn },
        activation_date: { value: parseDateField(device?.activation_date) },
        address: { value: device?.address || "" },
        system_age: { value: device?.system_age },
        rowSettings: {
          isRowClickable: true,
          id: perftest?.perftest_result_id,
          item: { perftest, device, client_id: client?.id, phisical_status: "installed", sn: device?.sn },
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDataToMonitoringTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(device => {
    const {
      id,
      email,
      perftest,
      sn,
      homeowner_name,
      country,
      city,
      state,
      zip,
      work_status,
      name,
      address,
      hvac,
      alerts,
      alert_rules,
      membership,
      memberships,
      system_type,
      accessories,
      system_age,
    } = device

    const perfTestStatus = perftest?.status?.toLowerCase()

    return {
      email: { value: email },
      perftest_status: { value: ["passed", "issue", "running"].includes(perfTestStatus) ? perfTestStatus : null },
      work_status: { value: work_status },
      country: { value: country?.name },
      city: { value: city?.name },
      zip_code: { value: zip?.code },
      state: { value: state?.name },
      homeowner_name: { value: homeowner_name },
      sn: { value: sn },
      name: { value: name },
      address: { value: address || "" },
      installation_type: { value: hvac },
      alerts: { value: alerts },
      alert_rules: { value: alert_rules || [] },
      membership: { value: membership },
      membership_name: { value: memberships },
      system_type: { value: parseSystemType(system_type) },
      accessories: { value: accessories },
      system_age: { value: system_age },
      rowSettings: {
        id,
        item: device,
      },
    } as UITypes.TableBodyRow
  })
}

export type MonitoringDevice = ApiTypes.Model.Device & { contractor_name: string }

export const convertEnterpriseThermostatToMonitoringDevice = (
  device: ApiTypes.Model.EnterpriseThermostat,
): MonitoringDevice => {
  const deviceFields = {
    id: device.id,
    sn: device.sn,
    name: device.name || "",
    perftest: device.perftest,
    system_age: device.system_age !== undefined && device.system_age !== null ? String(device.system_age) : "",
    system_type: device.system_type || "",
    activation_date: device.activation_date,
    membership: device.membership || "",
    memberships: device.memberships || [],
    homeowner_name: device.homeowner,
    work_status: device.work_status,
    client_id: device.client_id ?? null,
    address: device.address,
    hvac: device.installation_type,
    country: { name: device.country_name } as ApiTypes.Model.Country,
    city: { name: device.city } as ApiTypes.Model.City,
    state: { name: device.state } as ApiTypes.Model.State,
    zip: { code: device.zip_code || "" } as ApiTypes.Model.Zip,
    alerts: device.alerts || "",
    phisical_status: device.physical_status,
    email: device.email,
    accessories: device.accessories || "",
    time_zone_text: device.time_zone_text || "",
  } satisfies Partial<ApiTypes.Model.Device>

  return {
    ...deviceFields,
    contractor_name: device.contractor,
  } as unknown as MonitoringDevice
}

export const convertEnterpriseMonitoringToTableRows = (
  data?: ApiTypes.Model.EnterpriseThermostat[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  const normalizedDevices = data.map(convertEnterpriseThermostatToMonitoringDevice)

  return convertDataToMonitoringTableRows(normalizedDevices).map(
    (row, index) =>
      ({
        ...row,
        checkbox: {},
        contractor_name: { value: data[index]?.contractor || "" },
        activation_date: { value: parseDateField(data[index]?.activation_date) },
        rowSettings: {
          ...row.rowSettings,
          item: normalizedDevices[index],
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertMessagesBatchToTableRows = (data?: ApiTypes.Model.MessageBatch[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({
      id,
      title,
      content_rich,
      created_at,
      most_recent_delivery_timestamp,
      scheduled_delivery_timestamp,
      pending_delivery_count,
      failed_delivery_count,
      delivered_count,
    }) =>
      ({
        title: { value: title },
        content: { value: content_rich ? decodeToMarkDown(content_rich) : "" },
        date: { value: parseDateField(created_at) },
        most_recent_delivery_date: { value: parseDateField(most_recent_delivery_timestamp, "LLLL") },
        scheduled_delivery_date: { value: parseDateField(scheduled_delivery_timestamp, "LLLL") },
        delivered: { value: delivered_count },
        pending: { value: pending_delivery_count },
        failed: { value: failed_delivery_count },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertWHManagerOrdersNewToTableRows = (
  data?: ApiTypes.Model.WHManagerOrderNew[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, contractor_id, items, company_name, brand_name }) => {
    const singleItem = items.length === 1 ? items[0] : null

    return {
      group_toggle: { hidden: !!singleItem },
      item_id: { value: singleItem?.id },
      item_status: { value: singleItem?.status },
      company_name: { value: company_name },
      brand_name: { value: brand_name },
      quantity: { value: singleItem?.quantity },
      product_type: { value: getProductTypeLabel(singleItem?.product_type) },
      contractor_id: { value: contractor_id },
      subRows: singleItem
        ? []
        : items.map(item => ({
            group_toggle: { hidden: true },
            item_id: { value: item.id },
            item_status: { value: item.status },
            quantity: { value: item.quantity },
            product_type: { value: getProductTypeLabel(item.product_type) },
            brand_name: { value: null },
            company_name: { value: null },
            contractor_id: { value: null },
            rowSettings: {
              id: item.id,
              item: [item],
            },
          })),
      rowSettings: {
        id,
        item: items,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertWHManagerDevicessToTableRows = (
  data?: ApiTypes.Model.WHManagerDevice[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, sn, input_date, phisical_status }) =>
      ({
        checkbox: {},
        sn: { value: sn },
        input_date: { value: parseDateField(input_date) },
        phisical_status: { value: phisical_status },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertMessageReportToTableRows = (data?: ApiTypes.Model.MessageReportV2[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({
      id,
      sn,
      client_name,
      country_name,
      city_name,
      zip_code,
      state_name,
      activation_date,
      membership,
      residence_type,
      installation_type,
      accessories,
      system_type,
      system_age,
      messages,
    }) => {
      const singleMessage = messages.length === 1 ? messages[0] : null

      return {
        group_toggle: { hidden: singleMessage || !messages.length ? true : false },
        sn: { value: sn },
        client_name: { value: client_name },
        country: { value: country_name },
        city: { value: city_name },
        zip: { value: zip_code },
        state: { value: state_name },
        activation_date: { value: parseDateField(activation_date) },
        membership: { value: membership },
        residence_type: { value: residence_type },
        system_type: { value: system_type },
        accessories: { value: accessories },
        installation_type: { value: installation_type },
        delivery_status: { value: singleMessage?.delivery_status },
        system_age: { value: system_age },
        scheduled_at: { value: parseDateField(singleMessage?.scheduled_at) },
        delivered_at: { value: parseDateField(singleMessage?.delivered_at) },
        subRows: !singleMessage
          ? messages.map(message => ({
              group_toggle: { hidden: true },
              sn: { hidden: true },
              client_name: { hidden: true },
              country: { hidden: true },
              city: { hidden: true },
              zip: { hidden: true },
              state: { hidden: true },
              activation_date: { hidden: true },
              membership: { hidden: true },
              residence_type: { hidden: true },
              system_type: { hidden: true },
              accessories: { hidden: true },
              installation_type: { hidden: true },
              delivery_status: { value: message.delivery_status },
              system_age: { hidden: true },
              scheduled_at: { value: parseDateField(message.scheduled_at) },
              delivered_at: { value: parseDateField(message.delivered_at) },
              rowSettings: { id: message.id },
            }))
          : [],
        rowSettings: {
          id,
        },
      } as UITypes.TableBodyRow
    },
  )
}

export const convertUsersToTableRows = (data?: ApiTypes.Model.User[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, email, full_name, phone, role, created_by, creation_date }) =>
      ({
        name: { value: full_name },
        role: { value: transformString(role?.type) },
        email: { value: email },
        phone: { value: phone },
        creation_date: { value: parseDateField(creation_date) },
        created_by: { value: created_by },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertContractorUsersToTableRows = (data?: ApiTypes.Model.User[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, email, full_name, phone, role, is_enabled, created_by, creation_date }) =>
      ({
        name: { value: full_name },
        role: { value: contractorUserRoleOptions.find(option => option.value === role?.type)?.label || "" },
        email: { value: email },
        phone: { value: phone },
        creation_date: { value: parseDateField(creation_date) },
        created_by: { value: created_by },
        is_enabled: { value: is_enabled },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertValidatedDevicesToState = (data: ApiTypes.Model.InvalideDevice[]): UITypes.InvalidDevice[] => {
  if (!data?.length) return []

  return data.map((item, index) => ({
    sn: item?.sn ? item.sn : "",
    row: item?.row ? item.row : (index += 1),
    uid: item?.uid ? item.uid : uuidv4(),
    errorMessage: item?.error_message ? item?.error_message : "",
  }))
}

export const convertVendorDevicesToTableRows = (
  data?: ApiTypes.Model.VendorDevice[],
  headerCells?: UITypes.TableHeaderCell[],
  callback?: (id: number) => void,
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        sn: { value: device.sn, callback: () => callback && callback(device.id) },
        work_status: { value: device.work_status },
        phisical_status: { value: device.phisical_status },
        contractor_name: { value: device.contractor_name },
        activation_date: { value: parseDateField(device.activation_date) },
        red_flag: { value: `${device.red_flag ? "Yes" : "No"}` },
        red_flag_type: { value: device.red_flag_type },
        homeowner: { value: device.homeowner },
        address: { value: device.address },
        city: { value: device.city },
        state: { value: device.state },
        zip_code: { value: device.zip_code },
        country: { value: device.country_name },
        email: { value: device.email },
        phone: { value: device.phone },
        installation_type: { value: device.installation_type },
        device_location: { value: device.device_location },
        system_age: { value: device.system_age },
        rowSettings: {
          id: Number(device.id),
          isRedFlag: device.red_flag,
          item: device,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDeviceHistoryLog = (data: ApiTypes.Model.HistoryLog[]): UITypes.HistoryLog[] => {
  if (!data?.length) return []

  return data?.map(item => ({
    status: item?.status,
    author: item?.author,
    lastUpdate: item?.last_update,
  }))
}

export const convertEditDevicesToState = (data: ApiTypes.Model.Device[]): UITypes.Device[] => {
  if (!data?.length) return []

  const checkInstallationType = (value: string | undefined) => {
    switch (value) {
      case "new":
        return [
          {
            value: "new",
            label: "New",
          },
        ]
      case "existing":
        return [
          {
            value: "existing",
            label: "Existing",
          },
        ]
      default:
        return []
    }
  }

  return data?.map(item => ({
    _id: uuidv4(),
    id: item?.id ? item?.id : undefined,
    serialNumber: item?.sn ? item?.sn : "",
    address1: item?.address1 ? item?.address1 : "",
    address2: item?.address2 ? item?.address2 : "",
    country: convertDataToOptions({ data: [item?.country] }, "id", "name"),
    zip: convertDataToOptions({ data: [item?.zip] }, "id", "code"),
    state: convertDataToOptions({ data: [item?.state] }, "id", "name"),
    city: convertDataToOptions({ data: [item?.city] }, "id", "name"),
    suburb: convertDataToOptions({ data: [item?.suburb] }, "id", "name"),
    residentialType: item?.residence_type
      ? convertDataToOptions({ data: [item?.residence_type] }, "id", "name")
      : undefined,
    deviceLocation: convertDataToOptions({ data: [item?.location] }, "id", "name"),
    deviceName: item?.name || "",
    installationType: checkInstallationType(item.installation_type),
    workStatus: item?.work_status as UITypes.DeviceWorkStatus,
    systemAge: item?.system_age
      ? [
          {
            value: item?.system_age,
            label: `${item?.system_age} ${Number(item?.system_age) === 1 ? "Year" : "Years"}`,
          },
        ]
      : undefined,
  }))
}
